import React, { useEffect, useCallback, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useWhitelist } from '../context/WhitelistContext';
import { CustomWalletModal } from './CustomWalletModal';
import { useDisclosure } from '@chakra-ui/react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  useToast,
  Spinner,
  Center,
  Flex,
  Icon,
  Link,
  Image,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Input,
  InputGroup,
  InputRightElement
} from '@chakra-ui/react';
import { FaTwitter, FaDiscord, FaExternalLinkAlt } from 'react-icons/fa';
import { GiPalmTree, GiIsland, GiTreasureMap, GiCoins, GiChest, GiGoldStack, GiCompass, GiPirateSkull, GiShipBow, GiAnchor, GiShipWheel } from 'react-icons/gi';
import { RiPlantFill } from 'react-icons/ri';

/**
 * WhitelistGuard component
 * Protects routes that require whitelist access
 * Redirects non-whitelisted users to a message page
 * Also allows password bypass with "palm1"
 */
const WhitelistGuard = ({ children }) => {
  const { publicKey, connected, connect, select, wallet, wallets } = useWallet();
  const { isUserWhitelisted, isWhitelistLoaded } = useWhitelist();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // State for password input - moved to top level to follow Rules of Hooks
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Show toast when user is not whitelisted
  useEffect(() => {
    if (connected && publicKey && !isUserWhitelisted && isWhitelistLoaded) {
      toast({
        title: 'Access Restricted',
        description: 'Your wallet is not whitelisted for the auction.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top'
      });
    }
  }, [connected, publicKey, isUserWhitelisted, isWhitelistLoaded, toast]);

  // If whitelist is still loading, show loading spinner
  if (!isWhitelistLoaded) {
    return (
      <Center h="100vh" bg="black">
        <VStack spacing={6}>
          <Spinner size="xl" color="#00e676" thickness="4px" />
          <Text color="white">Checking whitelist access...</Text>
        </VStack>
      </Center>
    );
  }

  // Check if user has entered the correct password previously
  const hasPasswordBypass = localStorage.getItem('palmheist_password_bypass') === 'true';
  if (hasPasswordBypass) {
    return children;
  }
  
  // If user is not connected, prompt to connect wallet
  if (!connected) {
    return (
      <Box 
        h="100vh" 
        w="100%" 
        bg="black" 
        backgroundImage="radial-gradient(circle at center, rgba(0, 230, 118, 0.2), transparent 70%)"
        position="relative"
        overflow="hidden"
        pointerEvents="auto"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "radial-gradient(circle at center, rgba(0, 230, 118, 0.15), transparent 70%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      >
        {/* Animated palm trees in background */}
        <Box position="absolute" top="5%" left="5%" opacity="0.3" animation="float 8s ease-in-out infinite">
          <Icon as={GiPalmTree} w={24} h={24} color="#00e676" />
        </Box>
        <Box position="absolute" bottom="10%" right="8%" opacity="0.3" animation="float 10s ease-in-out infinite 1s">
          <Icon as={GiPalmTree} w={20} h={20} color="#00e676" />
        </Box>
        <Box position="absolute" top="40%" left="15%" opacity="0.25" animation="float 11s ease-in-out infinite 0.7s">
          <Icon as={GiIsland} w={20} h={20} color="#00e676" />
        </Box>
        <Box position="absolute" bottom="30%" right="20%" opacity="0.25" animation="float 9s ease-in-out infinite 1.2s">
          <Icon as={GiTreasureMap} w={20} h={20} color="#F6AD55" />
        </Box>
        {/* New pirate-themed icons */}
        <Box position="absolute" top="20%" right="15%" opacity="0.25" animation="float 12s ease-in-out infinite 0.5s">
          <Icon as={GiCompass} w={22} h={22} color="#F6AD55" />
        </Box>
        <Box position="absolute" bottom="20%" left="10%" opacity="0.25" animation="float 10s ease-in-out infinite 1.5s">
          <Icon as={GiPirateSkull} w={18} h={18} color="#FFFFFF" />
        </Box>
        <Box position="absolute" top="70%" left="25%" opacity="0.2" animation="float 13s ease-in-out infinite 0.3s">
          <Icon as={GiShipBow} w={30} h={30} color="#F6AD55" />
        </Box>
        {/* Decorative Pirate Icons in Background */}
        <Box position="absolute" top="20px" left="20px" opacity="0.25" zIndex="0">
          <Icon as={GiCompass} color="#00e676" boxSize={10} />
        </Box>
        <Box position="absolute" bottom="20px" right="20px" opacity="0.25" zIndex="0">
          <Icon as={GiAnchor} color="#00e676" boxSize={10} />
        </Box>
        <Box position="absolute" top="20px" right="20px" opacity="0.25" zIndex="0">
          <Icon as={GiShipWheel} color="#00e676" boxSize={10} />
        </Box>
        <Box position="absolute" bottom="20px" left="20px" opacity="0.25" zIndex="0">
          <Icon as={GiTreasureMap} color="#00e676" boxSize={10} />
        </Box>
        
        <Center h="100%" zIndex="1" pointerEvents="auto">
          <Box
            maxW="650px"
            w="90%"
            p={8}
            borderRadius="xl"
            bg="rgba(0, 0, 0, 0.75)"
            border="2px solid"
            borderColor="rgba(0, 230, 118, 0.6)"
            boxShadow="0 0 60px rgba(0, 230, 118, 0.32)"
            textAlign="center"
            position="relative"
            overflow="hidden"
            pointerEvents="auto"
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bg: "radial-gradient(circle at center, rgba(0, 230, 118, 0.15), transparent 70%)",
              pointerEvents: "none",
              zIndex: 0
            }}
          >
            <VStack spacing={8} position="relative" zIndex="1" pointerEvents="auto">
              <Heading 
                color="#00e676" 
                size="xl" 
                fontWeight="bold"
                textShadow="0 0 10px rgba(0, 230, 118, 0.4)"
                letterSpacing="wider"
              >
                Unlock the Myth <Icon as={GiPalmTree} ml={2} color="#00e676" />
              </Heading>
              
              <Text 
                color="#ffffff" 
                fontSize="xl" 
                fontWeight="medium"
                lineHeight="1.7"
                textShadow="0 0 8px rgba(0, 230, 118, 0.24)"
                px={4}
              >
                The golden sands, the ocean bright—fortune favors those who fight!
              </Text>
              
              <Box 
                w="full" 
                p={5} 
                borderRadius="xl" 
                bg="rgba(0, 230, 118, 0.15)" 
                border="2px solid rgba(0, 230, 118, 0.4)"
                boxShadow="0 0 20px rgba(0, 230, 118, 0.16)"
              >
                <VStack spacing={5}>
                  <Text color="white" fontWeight="bold" fontSize="lg">
                    The whitelist calls, don't stand behind
                  </Text>
                  
                  <HStack spacing={6} w="full" justifyContent="center" pointerEvents="auto">
                    <Button
                      as="a"
                      href="https://x.com/palmheist"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outline"
                      bg="rgba(29, 161, 242, 0.15)"
                      color="#ffffff"
                      borderColor="#1DA1F2"
                      borderWidth="2px"
                      borderRadius="full"
                      px={8}
                      py={6}
                      leftIcon={<FaTwitter size="20px" />}
                      _hover={{
                        bg: "rgba(29, 161, 242, 0.3)",
                        transform: "translateY(-3px)",
                        boxShadow: "0 6px 20px rgba(29, 161, 242, 0.4)"
                      }}
                      transition="all 0.3s"
                      fontSize="md"
                      fontWeight="bold"
                      cursor="pointer"
                      pointerEvents="auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open("https://x.com/palmheist", "_blank", "noopener,noreferrer");
                      }}
                    >
                      Follow on Twitter
                    </Button>
                    
                    <Button
                      as="a"
                      href="https://discord.gg/8GnWVtKth6"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outline"
                      bg="rgba(114, 137, 218, 0.15)"
                      color="#ffffff"
                      borderColor="#7289DA"
                      borderWidth="2px"
                      borderRadius="full"
                      px={8}
                      py={6}
                      leftIcon={<FaDiscord size="20px" />}
                      _hover={{
                        bg: "rgba(114, 137, 218, 0.3)",
                        transform: "translateY(-3px)",
                        boxShadow: "0 6px 20px rgba(114, 137, 218, 0.4)"
                      }}
                      transition="all 0.3s"
                      fontSize="md"
                      fontWeight="bold"
                      cursor="pointer"
                      pointerEvents="auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open("https://discord.gg/8GnWVtKth6", "_blank", "noopener,noreferrer");
                      }}
                    >
                      Join Discord
                    </Button>
                  </HStack>
                </VStack>
              </Box>
              
              <Divider borderColor="rgba(0, 230, 118, 0.3)" borderWidth="1px" />
              
              <Text 
                color="#ffffff" 
                fontSize="lg" 
                fontWeight="medium"
                textShadow="0 0 8px rgba(0, 230, 118, 0.24)"
              >
                Palm Trees, Ocean Breeze, Salty air, Sun Kisses Hair, let US take You There!
              </Text>
              
              <Box 
                mb={4}
                w="full"
                display="flex"
                justifyContent="center"
              >
                <Button
                  bg="rgba(0, 230, 118, 0.15)"
                  border="2px solid #00e676"
                  color="#ffffff"
                  borderRadius="30px"
                  fontSize="18px"
                  fontWeight="600"
                  height="56px"
                  px={8}
                  _hover={{
                    bg: "rgba(0, 230, 118, 0.25)",
                    transform: "translateY(-3px)",
                    boxShadow: "0 6px 20px rgba(0, 230, 118, 0.32)"
                  }}
                  transition="all 0.3s"
                  textShadow="0 0 8px rgba(0, 230, 118, 0.4)"
                  cursor="pointer"
                  zIndex={1000}
                  onClick={onOpen}
                >
                  Select Wallet
                </Button>
              </Box>
              
              <Button 
                colorScheme="purple" 
                variant="outline"
                bg="rgba(153, 69, 255, 0.15)"
                color="#ffffff"
                borderColor="#9945FF"
                borderWidth="2px"
                borderRadius="full"
                px={8}
                py={5}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/');
                }}
                _hover={{
                  bg: "rgba(153, 69, 255, 0.3)",
                  transform: "translateY(-3px)",
                  boxShadow: "0 6px 20px rgba(153, 69, 255, 0.4)"
                }}
                transition="all 0.3s"
                fontSize="md"
                fontWeight="bold"
                textShadow="0 0 8px rgba(153, 69, 255, 0.24)"
                pointerEvents="auto"
                position="relative"
                zIndex={100}
              >
                Back to Home
              </Button>
            </VStack>
          </Box>
        </Center>
        
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
          <ModalOverlay backdropFilter="blur(10px)" />
          <ModalContent 
            bgGradient="linear(to-br, #0D1019, #1A202C)"
            borderRadius="xl"
            borderWidth="1px"
            borderColor="rgba(0, 230, 118, 0.3)"
            boxShadow={`0 0 20px rgba(0, 230, 118, 0.2)`}
            overflow="hidden"
            p={0}
            position="relative"
          >
            {/* Custom close button in the top right corner */}
            <Box
              position="absolute"
              top="8px"
              right="8px"
              zIndex="100"
              cursor="pointer"
              onClick={onClose}
              bg="rgba(0, 0, 0, 0.2)"
              borderRadius="full"
              w="32px"
              h="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{
                bg: "rgba(0, 230, 118, 0.2)",
                color: "#00e676"
              }}
            >
              <Text fontSize="lg" fontWeight="bold" color="white">×</Text>
            </Box>
            
            <Box position="relative">
              {/* Decorative Pirate Icons in Background */}
              <Box position="absolute" top="20px" left="20px" opacity="0.15" zIndex="0">
                <Icon as={GiCompass} color="#00e676" boxSize={8} />
              </Box>
              <Box position="absolute" bottom="20px" right="20px" opacity="0.15" zIndex="0">
                <Icon as={GiAnchor} color="#00e676" boxSize={8} />
              </Box>
              <Box position="absolute" top="20px" right="20px" opacity="0.15" zIndex="0">
                <Icon as={GiShipWheel} color="#00e676" boxSize={8} />
              </Box>
              <Box position="absolute" bottom="20px" left="20px" opacity="0.15" zIndex="0">
                <Icon as={GiTreasureMap} color="#00e676" boxSize={8} />
              </Box>
              
              <ModalHeader textAlign="center" color="white" position="relative" zIndex="1">
                Connect Your Wallet
              </ModalHeader>
              <ModalBody py={6} position="relative" zIndex="1">
                <VStack spacing={4} align="stretch">
                  <Text fontSize="sm" color="whiteAlpha.700" textAlign="center" mb={2}>
                    Connect with one of our available wallet providers to continue your pirate adventure
                  </Text>
                  
                  {wallets.map((wallet) => (
                    <Button
                      key={wallet.adapter.name}
                      onClick={async () => {
                        try {
                          await select(wallet.adapter.name);
                          // Add a small delay before connecting
                          setTimeout(async () => {
                            try {
                              await connect();
                              console.log('Wallet connected successfully');
                              // Check if user is whitelisted after connection
                              if (publicKey && isUserWhitelisted) {
                                toast({
                                  title: 'Access Granted',
                                  description: 'Your wallet is whitelisted. Welcome to Palm Heist!',
                                  status: 'success',
                                  duration: 5000,
                                  isClosable: true,
                                });
                              }
                            } catch (error) {
                              console.error('Failed to connect wallet:', error);
                              toast({
                                title: 'Connection Failed',
                                description: 'Failed to connect wallet. Please try again.',
                                status: 'error',
                                duration: 5000,
                                isClosable: true,
                              });
                            }
                          }, 500);
                        } catch (error) {
                          console.error('Failed to select wallet:', error);
                        }
                        onClose();
                      }}
                      variant="outline"
                      justifyContent="flex-start"
                      alignItems="center"
                      py={3}
                      px={4}
                      borderRadius="lg"
                      borderColor="whiteAlpha.200"
                      bg="whiteAlpha.50"
                      color="white"
                      _hover={{
                        bg: "whiteAlpha.100",
                        borderColor: "#00e676",
                      }}
                      leftIcon={
                        <Image 
                          src={wallet.adapter.icon} 
                          alt={wallet.adapter.name} 
                          boxSize="24px"
                          fallback={<Icon as={GiPirateSkull} color="#00e676" boxSize={6} />}
                        />
                      }
                    >
                      {wallet.adapter.name}
                    </Button>
                  ))}
                </VStack>
              </ModalBody>
            </Box>
          </ModalContent>
        </Modal>
      </Box>
    );
  }

  
  const handlePasswordSubmit = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      if (password === 'palm1') {
        // Store in localStorage that this user has entered correct password
        localStorage.setItem('palmheist_password_bypass', 'true');
        toast({
          title: 'Access granted',
          description: 'Welcome to Palm Heist!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        // Redirect to dashboard
        navigate('/dashboard');
      } else {
        toast({
          title: 'Incorrect password',
          description: 'Please try again or follow social media for updates',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      setIsSubmitting(false);
    }, 800);
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePasswordSubmit();
    }
  };
  
  // If user is connected but not whitelisted, show access denied message
  if (connected && publicKey && !isUserWhitelisted) {
    return (
      <Box 
        h="100vh" 
        w="100%" 
        bg="black" 
        backgroundImage="radial-gradient(circle at center, rgba(255, 94, 94, 0.2), transparent 70%)"
        position="relative"
        overflow="hidden"
        pointerEvents="auto"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "radial-gradient(circle at center, rgba(255, 94, 94, 0.15), transparent 70%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      >
        {/* Animated palm trees in background */}
        <Box position="absolute" top="5%" left="5%" opacity="0.3" animation="float 8s ease-in-out infinite">
          <Icon as={GiPalmTree} w={24} h={24} color="#00e676" />
        </Box>
        <Box position="absolute" bottom="10%" right="8%" opacity="0.3" animation="float 10s ease-in-out infinite 1s">
          <Icon as={GiPalmTree} w={20} h={20} color="#00e676" />
        </Box>
        <Box position="absolute" top="40%" left="15%" opacity="0.25" animation="float 11s ease-in-out infinite 0.7s">
          <Icon as={GiCoins} w={20} h={20} color="#F6AD55" />
        </Box>
        <Box position="absolute" bottom="30%" right="20%" opacity="0.25" animation="float 9s ease-in-out infinite 1.2s">
          <Icon as={GiGoldStack} w={20} h={20} color="#F6AD55" />
        </Box>
        {/* Decorative Pirate Icons in Background */}
        <Box position="absolute" top="20px" left="20px" opacity="0.25" zIndex="0">
          <Icon as={GiCompass} color="#ff5e5e" boxSize={10} />
        </Box>
        <Box position="absolute" bottom="20px" right="20px" opacity="0.25" zIndex="0">
          <Icon as={GiAnchor} color="#ff5e5e" boxSize={10} />
        </Box>
        <Box position="absolute" top="20px" right="20px" opacity="0.25" zIndex="0">
          <Icon as={GiShipWheel} color="#ff5e5e" boxSize={10} />
        </Box>
        <Box position="absolute" bottom="20px" left="20px" opacity="0.25" zIndex="0">
          <Icon as={GiTreasureMap} color="#ff5e5e" boxSize={10} />
        </Box>
        
        <Center h="100%" zIndex="1" pointerEvents="auto">
          <Box
            maxW="650px"
            w="90%"
            p={8}
            borderRadius="xl"
            bg="rgba(20, 20, 20, 0.85)"
            border="2px solid"
            borderColor="rgba(255, 94, 94, 0.6)"
            boxShadow="0 0 60px rgba(255, 94, 94, 0.32)"
            textAlign="center"
            position="relative"
            overflow="hidden"
            pointerEvents="auto"
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bg: "radial-gradient(circle at center, rgba(255, 94, 94, 0.15), transparent 70%)",
              pointerEvents: "none",
              zIndex: 0
            }}
          >
            <VStack spacing={8} position="relative" zIndex="1" pointerEvents="auto">
              <Heading 
                color="#ff5e5e" 
                size="xl" 
                fontWeight="bold"
                textShadow="0 0 10px rgba(255, 94, 94, 0.4)"
                letterSpacing="wider"
              >
                Access Denied
              </Heading>
              
              <Text 
                color="#ffffff" 
                fontSize="xl" 
                fontWeight="medium"
                lineHeight="1.7"
                textShadow="0 0 8px rgba(255, 94, 94, 0.24)"
                px={4}
              >
                Don't run and hide—join the crew and get inside!
              </Text>
              
              <Box 
                w="full" 
                p={5} 
                borderRadius="xl" 
                bg="rgba(255, 94, 94, 0.15)" 
                border="2px solid rgba(255, 94, 94, 0.4)"
                boxShadow="0 0 20px rgba(255, 94, 94, 0.16)"
              >
                <VStack spacing={5}>
                  <Text color="white" fontWeight="bold" fontSize="lg">
                    No way in? Follow to begin!
                  </Text>
                  
                  <HStack spacing={6} w="full" justifyContent="center" pointerEvents="auto">
                    <Button
                      as="a"
                      href="https://x.com/palmheist"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outline"
                      bg="rgba(29, 161, 242, 0.15)"
                      color="#ffffff"
                      borderColor="#1DA1F2"
                      borderWidth="2px"
                      borderRadius="full"
                      px={8}
                      py={6}
                      leftIcon={<FaTwitter size="20px" />}
                      _hover={{
                        bg: "rgba(29, 161, 242, 0.3)",
                        transform: "translateY(-3px)",
                        boxShadow: "0 6px 20px rgba(29, 161, 242, 0.4)"
                      }}
                      transition="all 0.3s"
                      fontSize="md"
                      fontWeight="bold"
                      cursor="pointer"
                      pointerEvents="auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open("https://x.com/palmheist", "_blank", "noopener,noreferrer");
                      }}
                    >
                      Follow on Twitter
                    </Button>
                    
                    <Button
                      as="a"
                      href="https://discord.gg/8GnWVtKth6"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outline"
                      bg="rgba(114, 137, 218, 0.15)"
                      color="#ffffff"
                      borderColor="#7289DA"
                      borderWidth="2px"
                      borderRadius="full"
                      px={8}
                      py={6}
                      leftIcon={<FaDiscord size="20px" />}
                      _hover={{
                        bg: "rgba(114, 137, 218, 0.3)",
                        transform: "translateY(-3px)",
                        boxShadow: "0 6px 20px rgba(114, 137, 218, 0.4)"
                      }}
                      transition="all 0.3s"
                      fontSize="md"
                      fontWeight="bold"
                      cursor="pointer"
                      pointerEvents="auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open("https://discord.gg/8GnWVtKth6", "_blank", "noopener,noreferrer");
                      }}
                    >
                      Join Discord
                    </Button>
                  </HStack>
                  
                  <Divider my={2} borderColor="rgba(255, 255, 255, 0.2)" />
                  
                  <Text color="white" fontSize="sm" textAlign="center">
                    Or enter password to continue:
                  </Text>
                  
                  <InputGroup size="md">
                    <Input
                      pr="4.5rem"
                      type="password"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      bg="rgba(0, 0, 0, 0.3)"
                      color="white"
                      borderColor="rgba(255, 255, 255, 0.3)"
                      _hover={{ borderColor: "rgba(255, 255, 255, 0.5)" }}
                      _focus={{ borderColor: "#ff5555", boxShadow: "0 0 0 1px #ff5555" }}
                    />
                    <InputRightElement width="4.5rem">
                      <Button 
                        h="1.75rem" 
                        size="sm" 
                        onClick={handlePasswordSubmit}
                        isLoading={isSubmitting}
                        bg="#ff5555"
                        color="white"
                        _hover={{ bg: "#ff3333" }}
                      >
                        Enter
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </VStack>
              </Box>
              
              <Divider borderColor="rgba(255, 94, 94, 0.3)" borderWidth="1px" />
              
              <Text color="gray.400" fontSize="sm">
                Wallet: {publicKey.toString()}
              </Text>
              
              <Text 
                color="#ffffff" 
                fontSize="lg" 
                fontWeight="medium"
                textShadow="0 0 8px rgba(255, 94, 94, 0.24)"
              >
                While they wait, you're stacking high—whitelisted winners touch the sky!
              </Text>
              
              <Button 
                colorScheme="purple" 
                variant="outline"
                bg="rgba(153, 69, 255, 0.15)"
                color="#ffffff"
                borderColor="#9945FF"
                borderWidth="2px"
                borderRadius="full"
                px={8}
                py={5}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/');
                }}
                _hover={{
                  bg: "rgba(153, 69, 255, 0.3)",
                  transform: "translateY(-3px)",
                  boxShadow: "0 6px 20px rgba(153, 69, 255, 0.4)"
                }}
                transition="all 0.3s"
                fontSize="md"
                fontWeight="bold"
                textShadow="0 0 8px rgba(153, 69, 255, 0.24)"
                pointerEvents="auto"
                position="relative"
                zIndex={100}
              >
                Back to Home
              </Button>
            </VStack>
          </Box>
        </Center>
      </Box>
    );
  }

  // If user is whitelisted, allow access to the protected route
  return children;
};

export default WhitelistGuard;
